import type { AbsenceLadderDto, UserDto } from '@bqcrm/shared';
import { ActionIcon, Alert, Badge, Button, Group, Select, Stack, Text } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconStairsUp, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';

/**
 * Karta pracownika → Akceptacja urlopów: kto i w jakiej kolejności zatwierdza wnioski o nieobecność (docs/17).
 * Nieobecny zatwierdzający — wniosek idzie do jego zastępcy (Zastępstwa), a bez zastępcy — poziom wyżej.
 */
export function AbsenceLadderTab({ user, users, readOnly, onDirty, maw }: { user: UserDto; users: UserDto[]; readOnly: boolean; onDirty: (d: boolean) => void; maw: number }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['absence-ladder', user.id], queryFn: () => api<AbsenceLadderDto>(`/users/${user.id}/absence-ladder`) });
  const [ids, setIds] = useState<string[]>([]);
  const [pick, setPick] = useState<string | null>(null);
  const saved = q.data?.approvers.map((a) => a.approverId) ?? [];
  const dirty = !!q.data && ids.join() !== saved.join();
  useEffect(() => {
    if (q.data) setIds(q.data.approvers.map((a) => a.approverId));
  }, [q.data]);
  useEffect(() => onDirty(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirty(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api<AbsenceLadderDto>(`/users/${user.id}/absence-ladder`, { method: 'PUT', body: { approverIds: ids } }),
    onSuccess: (r) => {
      qc.setQueryData(['absence-ladder', user.id], r);
      notifyOk('Zapisano drabinkę akceptacji');
    },
    onError: notifyError,
  });
  const name = (id: string) => q.data?.approvers.find((a) => a.approverId === id)?.fullName ?? users.find((u) => u.id === id)?.fullName ?? '—';
  const inactive = (id: string) => q.data?.approvers.find((a) => a.approverId === id)?.active === false;
  const options = users.filter((u) => u.active && u.hasAccount && u.id !== user.id && !ids.includes(u.id)).map((u) => ({ value: u.id, label: u.fullName }));
  const move = (i: number, d: -1 | 1) => {
    const next = [...ids];
    [next[i], next[i + d]] = [next[i + d]!, next[i]!];
    setIds(next);
  };

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Stack maw={maw} gap="md">
        <SectionCard
          title="Drabinka akceptacji wniosków o nieobecność"
          icon={<IconStairsUp size={16} />}
          footer={
            !readOnly && (
              <Group justify="flex-end">
                <Button variant="default" disabled={!dirty} onClick={() => setIds(saved)}>
                  Anuluj
                </Button>
                <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
                  Zapisz
                </Button>
              </Group>
            )
          }
        >
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Wniosek pracownika trafia kolejno do tych osób — po akceptacji do następnej, aż do ostatniej. Odmowa na dowolnym poziomie kończy wniosek (z powodem widocznym w aplikacji mobilnej). Gdy osoba
              zatwierdzająca jest na urlopie, decyduje jej zastępca (Moje zadania → Zastępstwa), a bez zastępcy — kolejny poziom. Wymiaru urlopu system nie sprawdza.
            </Text>
            {!ids.length && <EmptyState compact title="Brak drabinki" hint="Wnioski tego pracownika rozpatrują osoby z uprawnieniem „Zatwierdzanie nieobecności”." />}
            {ids.map((id, i) => (
              <Group key={id} justify="space-between" wrap="nowrap">
                <Group gap="xs" wrap="nowrap">
                  <Badge variant="light" size="lg" circle>
                    {i + 1}
                  </Badge>
                  <Text size="sm" fw={500}>
                    {name(id)}
                  </Text>
                  {i === ids.length - 1 && ids.length > 1 && (
                    <Badge size="sm" variant="light" color="gray">
                      decyzja końcowa
                    </Badge>
                  )}
                  {inactive(id) && (
                    <Badge size="sm" color="orange" variant="light">
                      konto nieaktywne — pomijane
                    </Badge>
                  )}
                </Group>
                {!readOnly && (
                  <Group gap={2} wrap="nowrap">
                    <ActionIcon variant="subtle" aria-label="Wyżej" disabled={i === 0} onClick={() => move(i, -1)}>
                      <IconArrowUp size={16} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" aria-label="Niżej" disabled={i === ids.length - 1} onClick={() => move(i, 1)}>
                      <IconArrowDown size={16} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" color="red" aria-label="Usuń z drabinki" onClick={() => setIds(ids.filter((x) => x !== id))}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                )}
              </Group>
            ))}
            {!readOnly && ids.length < 6 && (
              <Group align="flex-end" wrap="wrap">
                <Select label="Dodaj osobę zatwierdzającą" placeholder="Wybierz pracownika z kontem" searchable data={options} value={pick} onChange={setPick} w={{ base: '100%', sm: 320 }} />
                <Button
                  size="xs"
                  variant="light"
                  disabled={!pick}
                  mb={4}
                  onClick={() => {
                    if (pick) setIds([...ids, pick]);
                    setPick(null);
                  }}
                >
                  Dodaj poziom
                </Button>
              </Group>
            )}
          </Stack>
        </SectionCard>
        {!!q.data?.fallbackNames.length && (
          <Alert color="gray" variant="light">
            Gdy drabinka jest pusta albo nikt z niej nie jest dostępny, wniosek rozpatrują: {q.data.fallbackNames.join(', ')}.
          </Alert>
        )}
      </Stack>
    </QueryState>
  );
}
